import type { AllDexsAssetCtxsEvent } from '@nktkas/hyperliquid/api/subscription';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { subscribeWhileMounted } from '@/lib/hyperliquid/client';
import type { PerpMarketCtx } from '@/lib/hyperliquid/types';

import {
  perpsContextSelector,
  perpsContextsSelector,
  usePerpsExchangeStore,
} from '@/store/perps/exchange';
import { usePerpsOrderFormStore } from '@/store/perps/orderForm';

import {
  type PerpDex,
  type PerpsMetadata,
  perpsMetadataQueryOptions,
  usePerpDexs,
} from './usePerpDexs';
import { usePerpsAccountData } from './usePerpsAccountFeed';

const EMPTY_MARKETS: PerpsMetadata['marketByCoin'] = {};
const selectMarkets = (data: PerpsMetadata) => data.marketByCoin;

export function usePerpsMarkets() {
  return useQuery({ ...perpsMetadataQueryOptions(), select: selectMarkets }).data ?? EMPTY_MARKETS;
}

export function usePerpsMarketContexts() {
  return usePerpsExchangeStore(perpsContextsSelector);
}

export function useSelectedPerpsMarket() {
  const coin = usePerpsOrderFormStore((state) => state.selectedCoin);
  const markets = usePerpsMarkets();
  const ctx = usePerpsExchangeStore(perpsContextSelector(coin));
  const market = Object.hasOwn(markets, coin) ? markets[coin] : undefined;
  return { coin, market, ctx, assetIndex: market?.assetIndex };
}

export function marketContextsByCoin(dexs: PerpDex[], event: AllDexsAssetCtxsEvent) {
  const contextsByDex = new Map(event.ctxs);
  const ctxByCoin: Record<string, PerpMarketCtx> = {};
  for (const dex of dexs) {
    const contexts = contextsByDex.get(dex.name);
    dex.meta.universe.forEach((market, index) => {
      const ctx = contexts?.[index];
      if (ctx) ctxByCoin[market.name] = ctx;
    });
  }
  return ctxByCoin;
}

/** Mount once per page. Contexts include every supported market, regardless of selection. */
export function usePerpsMarketFeed() {
  const dexs = usePerpDexs();
  const selectedCoin = usePerpsOrderFormStore((state) => state.selectedCoin);
  const positions = usePerpsAccountData((account) => account.positions);
  // Stable dep for the set of coins that get the fast stream.
  const activeCoins = JSON.stringify(
    [...new Set([selectedCoin, ...positions.map((position) => position.coin)])].sort(),
  );

  useEffect(() => {
    if (!dexs) return;
    const cleanup = subscribeWhileMounted((client, options) =>
      client.allDexsAssetCtxs((event) => {
        if (options.signal?.aborted) return;
        usePerpsExchangeStore.getState().setContexts(marketContextsByCoin(dexs, event));
      }, options),
    );
    return () => {
      cleanup();
      usePerpsExchangeStore.getState().clearContexts();
    };
  }, [dexs]);

  useEffect(() => {
    if (!dexs) return;
    const markets = new Set(dexs.flatMap((dex) => dex.meta.universe.map((market) => market.name)));
    const coins = (JSON.parse(activeCoins) as string[]).filter((coin) => markets.has(coin));
    // A mainnet sample on 2026-09-25 measured ~15s bulk updates versus ~1s active
    // updates. Keep the faster feed for both the selected market and held positions.
    const cleanups = coins.map((coin) =>
      subscribeWhileMounted((client, options) =>
        client.activeAssetCtx(
          { coin },
          (event) => {
            if (options.signal?.aborted || event.coin !== coin || !('funding' in event.ctx)) return;
            usePerpsExchangeStore.getState().setActiveContext(coin, event.ctx);
          },
          options,
        ),
      ),
    );
    return () => {
      cleanups.forEach((cleanup) => cleanup());
      coins.forEach((coin) => usePerpsExchangeStore.getState().removeActiveContext(coin));
    };
  }, [activeCoins, dexs]);
}
